import { t } from '@/lib/i18n';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/format';
import { useSuggest } from '@/lib/queries';
import type { Suggestion } from '@/lib/api';

/**
 * Type-ahead search in the left rail, over `/api/search/suggest`.
 *
 * Suggestions are drawn from what the library actually contains — brands, AI
 * hashtags, image types, filenames and artwork product codes — so what you can
 * search for is always what is really there.
 *
 * Selecting a suggestion navigates to the search page using the *field* it came
 * from: picking the brand "Clipper" filters by brand rather than running a text
 * search for the word, which is the whole reason the endpoint returns `kind`.
 * Free text (or Enter with nothing highlighted) falls back to a plain query.
 */

/** How each kind is labelled, and which search param it drives. */
const kinds = (): Record<
  Suggestion['kind'],
  { label: string; param: 'q' | 'brand' | 'imageType' | 'path' }
> => ({
  brand: { label: t('railSearch.brand'), param: 'brand' },
  imageType: { label: t('railSearch.type'), param: 'imageType' },
  tag: { label: t('railSearch.tag'), param: 'q' },
  filename: { label: t('railSearch.file'), param: 'q' },
  product: { label: t('railSearch.code'), param: 'q' },
  folder: { label: t('railSearch.folder'), param: 'path' },
});

/** Keystroke-to-request delay. Long enough to skip most intermediate states. */
const DEBOUNCE_MS = 200;

export function RailSearch() {
  const navigate = useNavigate();
  const [raw, setRaw] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);

  // Debounce: the endpoint is rate limited and fans out across several queries,
  // so it should see typing pauses, not keystrokes.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(raw), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [raw]);

  const { data: items = [], isFetching } = useSuggest(debounced);

  // A changed result set invalidates the highlight — index 3 of the old list is
  // not index 3 of the new one.
  useEffect(() => setActive(-1), [items]);

  // Close when focus leaves the box entirely (click elsewhere, or tab away).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function go(s: Suggestion) {
    const { param } = kinds()[s.kind];
    navigate({ to: '/search', search: { [param]: s.value } });
    setOpen(false);
    setRaw('');
  }

  function goFreeText() {
    const q = raw.trim();
    if (q.length < 2) return;
    navigate({ to: '/search', search: { q } });
    setOpen(false);
    setRaw('');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'Enter') {
      // Enter takes the highlighted suggestion if there is one, otherwise the
      // literal text — so typing a term and hitting Enter always does something.
      if (open && active >= 0 && items[active]) go(items[active]);
      else goFreeText();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!items.length) return;
      e.preventDefault(); // stop the caret jumping to either end of the input
      setOpen(true);
      setActive((i) => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
        // Wrap, so the list is navigable without watching the boundaries.
        return next < 0 ? items.length - 1 : next >= items.length ? 0 : next;
      });
    }
  }

  const showList = open && debounced.trim().length >= 2;

  return (
    <div ref={boxRef} className="relative px-2 pt-2">
      <label className="relative block">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-white/40" />
        <input
          value={raw}
          onChange={(e) => { setRaw(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={t('railSearch.searchLibraryPlaceholder')}
          aria-label={t('railSearch.searchLibrary')}
          role="combobox"
          aria-expanded={showList}
          aria-controls="rail-suggestions"
          autoComplete="off"
          className="w-full rounded-lg bg-white/10 py-1.5 pl-8 pr-7 text-[13px] text-white placeholder:text-white/40 outline-none focus:bg-white/15 focus:ring-1 focus:ring-white/25"
        />
        {raw && (
          <button
            onClick={() => { setRaw(''); setOpen(false); }}
            className="absolute right-1.5 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded text-white/40 hover:bg-white/10 hover:text-white"
            aria-label={t('railSearch.clearSearch')}
            type="button"
          >
            <X className="size-3" />
          </button>
        )}
      </label>

      {showList && (
        <ul
          id="rail-suggestions"
          role="listbox"
          className="absolute inset-x-2 z-20 mt-1 max-h-80 overflow-y-auto rounded-lg border line surface-card py-1 shadow-lg"
        >
          {items.length === 0 ? (
            <li className="px-3 py-2 text-xs text-muted">
              {isFetching ? t('railSearch.searching') : t('railSearch.noMatches')}
            </li>
          ) : (
            items.map((s, i) => (
              <li key={`${s.kind}:${s.value}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  // Mouse-down rather than click: the input's blur would
                  // otherwise close the list before the click registers.
                  onMouseDown={(e) => { e.preventDefault(); go(s); }}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px]',
                    i === active ? 'bg-brand-50 dark:bg-white/10' : 'hover:bg-black/5 dark:hover:bg-white/5',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate text-strong">{s.value}</span>
                  <span className="shrink-0 text-[10px] uppercase tracking-wide text-faint">
                    {kinds()[s.kind].label}
                  </span>
                  {s.count > 0 && (
                    <span className="shrink-0 text-[11px] tabular-nums text-muted">{s.count}</span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
